/**
 * ReadyGate — a resettable promise that acts as a semaphore.
 *
 * While the gate is "closed" (e.g. IDB is reconnecting), any caller that
 * calls `gate.wait()` will suspend until `gate.open()` is called.
 * While the gate is already "open", `gate.wait()` resolves immediately.
 */
export class ReadyGate {
    private _promise: Promise<void>;
    private _resolve!: () => void;
    private _open = false;

    constructor(initiallyOpen = true) {
        this._promise = new Promise((res) => (this._resolve = res));
        if (initiallyOpen) this.open();
    }

    /** Resolves immediately if already open, otherwise waits. */
    wait(): Promise<void> {
        return this._promise;
    }

    /** Opens the gate — all pending waiters are unblocked. */
    open(): void {
        if (this._open) return;
        this._open = true;
        this._resolve();
    }

    /** Closes the gate — future waiters will block until open() is called. */
    close(): void {
        if (!this._open) return;
        this._open = false;
        this._promise = new Promise((res) => (this._resolve = res));
    }

    get isOpen(): boolean {
        return this._open;
    }
}
